import React, { useCallback, useState } from "react";
import { cn } from "../ui";
import ActionCenterDrawer from "./ActionCenterDrawer";
import useActionCenter from "./useActionCenter";

/**
 * ActionCenterPill — the whole guidance surface's footprint on the page.
 *
 * ── WHY A PILL AND NOT A PANEL ──────────────────────────────────────────────
 * This started as a card at the top of the dashboard and it was sized for the
 * wrong user. A brand-new institute's dashboard is eleven charts of zeroes, so
 * space there is free; an established institute's dashboard is full, so space
 * there is expensive. One block served both, which made it oversized in the
 * ninety-five per cent case — and it reached nobody who does not open the
 * dashboard, which is most counsellors.
 *
 * In the nav it costs one control, it is present on every page, and the
 * dashboard gets its vertical space back.
 *
 * ── WHY IT IS A LABELLED PILL AND NOT ANOTHER BELL ──────────────────────────
 * `NotificationBell` sits two elements away, and a second bell would be read as
 * more of the same — events that happened, to be cleared. This is the opposite:
 * a standing list of work that does not clear until the work is done. So it
 * carries a word ("to do"), a number, and the colour of the most urgent thing
 * behind it. An icon alone would have to be learned; "3 to do" does not.
 *
 * ── AND WHY IT DISAPPEARS ───────────────────────────────────────────────────
 * Nothing to say → nothing rendered, exactly as the panel version did. A
 * control that is permanently present and usually empty is one people stop
 * seeing, and then it is worthless on the day it is not empty. In practice it
 * is almost always present: an institute with no work outstanding still has
 * unused features in its plan.
 *
 * ⚠️ Renders nothing while loading, rather than a skeleton. A shimmer that
 * appears in the nav on every page load and resolves to nothing is worse than a
 * control that arrives a beat late — and this one shifts a right-aligned
 * cluster, where a late arrival costs nothing.
 */

// Static maps: a Tailwind class cannot be built at runtime — the compiler scans
// source text for whole class names, so `tw-bg-${tone}/10` is never seen and
// emits no CSS, with no error anywhere.
const PILL_TONE = {
  danger: "tw-border-danger/30 tw-bg-danger/10 tw-text-danger hover:tw-bg-danger/15",
  warning: "tw-border-warning/30 tw-bg-warning/10 tw-text-warning hover:tw-bg-warning/15",
  info: "tw-border-info/30 tw-bg-info/10 tw-text-info hover:tw-bg-info/15",
  success: "tw-border-success/30 tw-bg-success/10 tw-text-success hover:tw-bg-success/15",
  brand: "tw-border-primary/30 tw-bg-primary/10 tw-text-primary hover:tw-bg-primary/15",
};

const DOT_TONE = {
  danger: "tw-bg-danger",
  warning: "tw-bg-warning",
  info: "tw-bg-info",
  success: "tw-bg-success",
  brand: "tw-bg-primary",
};

const ActionCenterPill = () => {
  const state = useActionCenter();
  const [open, setOpen] = useState(false);
  const { loading, enabled, hasAnything, todoCount, discover, leadTone, reload } = state;

  const handleOpen = useCallback(() => {
    setOpen(true);
    // The one moment the numbers are about to be read closely. An admin who
    // assigned twelve leads and came back would otherwise be told they still
    // have twelve, and a to-do list that does not notice work being done is one
    // people stop believing.
    reload();
  }, [reload]);

  if (!enabled || loading) return null;

  /**
   * ⚠️ **The drawer outlives the pill, and it has to.**
   *
   * This was `if (… || !hasAnything) return null` for both, and it meant that
   * dismissing the LAST row while the drawer was open unmounted the drawer out
   * from under the person who had just clicked — the panel simply vanished
   * mid-interaction. It also made the drawer's "Nothing needs you" state
   * unreachable in the exact case it was written for.
   *
   * So emptiness hides the pill and leaves the drawer standing: the nav goes
   * quiet, and the reader gets told they are done. Closing it unmounts both.
   */
  if (!hasAnything && !open) return null;

  const tone = PILL_TONE[leadTone] ? leadTone : "brand";

  // Three registers, and the wording is the register. "5 to do" is work; "3
  // ideas" is not, and dressing the second as the first is how a nudge surface
  // gets muted. The count is only shown when it is a count of work — an "ideas"
  // badge with a number on it reads as unread mail.
  const isWork = todoCount > 0;
  const label = isWork
    ? "to do"
    : discover.length === 1
      ? "idea"
      : "ideas";
  const value = isWork ? todoCount : discover.length;

  return (
    <>
      {hasAnything && (
      <button
        type="button"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${value} ${isWork ? `thing${value === 1 ? "" : "s"} to do` : label} for this institute`}
        className={cn(
          "tw-inline-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-sm tw-border tw-border-solid tw-px-2.5 tw-text-xs tw-font-semibold tw-transition tw-focus-ring",
          PILL_TONE[tone],
        )}
      >
        <span
          className={cn("tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-[50%]", DOT_TONE[tone])}
          aria-hidden="true"
        />
        <span aria-hidden="true">{value}</span>
        {/* The word is what stops this being read as another notification
            counter. It is the first thing to go when the nav is cramped,
            because at that width the tone dot and the number still say
            "something is waiting" and the drawer says the rest. */}
        <span className="tw-hidden sm:tw-inline" aria-hidden="true">
          {label}
        </span>
      </button>
      )}

      <ActionCenterDrawer open={open} onClose={() => setOpen(false)} state={state} />
    </>
  );
};

export default ActionCenterPill;
